/*
 * cart-qty-fix.js
 * Single owner for cart line quantity changes (+ / - buttons and typed
 * quantity) in the cart drawer and on the /cart page.
 *
 * Why: two scripts handle the same +/- click today.
 *   1. Impulse theme.QtySelector (theme.js) - listener on the button:
 *        qty 1 -> 2, then /cart/change.js {quantity: 2}
 *   2. Custom inline CartManager (product-card snippet) - listener on document:
 *        reads 2 -> sets 3, fires "change" on the input, which triggers
 *        BOTH Impulse (/cart/change.js qty 3) and CartManager (/cart/change.js qty 3)
 *   So one click sent 3 concurrent requests (2, 3, 3) and a "-" click sent
 *   conflicting values, and the final quantity looked random.
 *
 * This file intercepts those events first (window, capture phase), stops the
 * duplicate handlers, and sends ONE /cart/change.js per line at a time.
 * Rapid clicks are queued so the final quantity always matches the input.
 * The drawer / cart page are then re-rendered by Impulse ("cart:build"),
 * so the Liquid markup, subtotal, discounts and empty state stay native.
 *
 * Load ONCE from layout/theme.liquid (after theme.js):
 *   <script src="{{ 'cart-qty-fix.js' | asset_url }}" defer></script>
 */
(function () {
  'use strict';

  if (window.__eolCartQtyFix) return;
  window.__eolCartQtyFix = true;

  var CART_FORM = 'form[action="/cart"], form[data-location]';
  var lines = {}; // key -> { desired, inFlight, input, wrapper }

  function changeUrl() {
    return (window.theme && theme.routes && theme.routes.cartChange) || '/cart/change.js';
  }

  function cartInputFrom(target) {
    var wrapper = target.closest('.js-qty__wrapper');
    if (!wrapper || !wrapper.closest(CART_FORM)) return null;
    var input = wrapper.querySelector('.js-qty__num');
    if (!input || !input.dataset.id) return null;
    return { wrapper: wrapper, input: input, key: input.dataset.id };
  }

  function minQty(input) {
    var m = parseInt(input.getAttribute('min'), 10);
    return isNaN(m) ? 0 : m;
  }

  function parseQty(value) {
    var n = parseInt(value, 10);
    return isNaN(n) || n < 0 ? null : n;
  }

  function formatMoney(cents) {
    if (window.theme && theme.Currency && theme.Currency.formatMoney && theme.settings) {
      return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  // Header / drawer counts that Impulse's rebuild does not touch.
  function updateCounts(cart) {
    var countEl = document.getElementById('CartItemCount');
    if (countEl) countEl.textContent = '(' + cart.item_count + ')';

    var bubble = document.querySelector('.cart-count-bubble');
    if (bubble) {
      var span = bubble.querySelector('span:not(.visually-hidden)');
      if (span) span.textContent = cart.item_count;
      bubble.setAttribute('data-count', cart.item_count);
    }

    var ship = document.querySelector('.shipping-progress-container');
    if (ship) {
      var threshold = parseInt(ship.dataset.threshold, 10) || 0;
      var bar = ship.querySelector('.progress-bar');
      if (bar && threshold) bar.style.width = Math.min(cart.total_price / threshold * 100, 100) + '%';
      var msg = ship.querySelector('.shipping-message');
      if (msg && threshold) {
        var needed = threshold - cart.total_price;
        msg.textContent = needed > 0
          ? 'Spend ' + formatMoney(needed) + ' more for free shipping'
          : 'You qualify for free shipping!';
      }
    }
  }

  function refreshCounts() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { updateCounts(cart); return cart; })
      .catch(function () {});
  }

  function setBusy(line, busy) {
    if (line.wrapper) line.wrapper.classList.toggle('is-loading', busy);
  }

  function send(key) {
    var line = lines[key];
    if (!line || line.inFlight) return;

    var qty = line.desired;
    line.inFlight = true;
    line.sent = qty;
    setBusy(line, true);

    fetch(changeUrl(), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({ id: key, quantity: qty })
    })
      .then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok || data.status) throw data;
          return data;
        });
      })
      .then(function (cart) {
        line.inFlight = false;
        if (line.desired !== line.sent) {
          send(key); // user clicked again while this request was running
          return;
        }
        delete lines[key];
        updateCounts(cart);
        // Let Impulse re-render drawer + cart page from Liquid (native markup).
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .catch(function (err) {
        line.inFlight = false;
        delete lines[key];
        console.warn('Cart quantity update failed:', err && (err.description || err.message) || err);
        // Re-sync UI with the real cart (e.g. inventory limit reached).
        refreshCounts();
        document.dispatchEvent(new CustomEvent('cart:build'));
      })
      .finally(function () {
        if (!lines[key]) setBusy(line, false);
      });
  }

  function requestQty(ctx, qty, force) {
    var min = minQty(ctx.input);
    if (qty < min && !force) qty = min;
    ctx.input.value = qty;
    ctx.input.dataset.lastValidValue = qty;

    var line = lines[ctx.key] || (lines[ctx.key] = { inFlight: false });
    line.desired = qty;
    line.input = ctx.input;
    line.wrapper = ctx.wrapper;
    send(ctx.key);
  }

  function current(ctx) {
    var line = lines[ctx.key];
    if (line && typeof line.desired === 'number') return line.desired;
    var q = parseQty(ctx.input.value);
    return q === null ? 1 : q;
  }

  // Line key from a remove link: data-id, or the id= param of its href.
  // The theme's Liquid href is "/cart/change ?id=KEY&quantity=0" (note the
  // stray space), which the browser turns into a 404 page - so we never
  // follow that link and remove the line with AJAX instead.
  function keyFromRemoveLink(link) {
    if (link.dataset.id) return link.dataset.id;
    var m = (link.getAttribute('href') || '').match(/[?&]id=([^&]+)/);
    return m ? decodeURIComponent(m[1]) : null;
  }

  // Delete (trash) icon in the drawer and on the /cart page
  window.addEventListener('click', function (event) {
    var link = event.target && event.target.closest &&
      event.target.closest('.cart__remove a, a.remove-item, a[href*="/cart/change"][href*="quantity=0"]');
    if (!link || !link.closest(CART_FORM)) return;

    var key = keyFromRemoveLink(link);
    if (!key) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    var item = link.closest('.cart__item, [data-key]');
    var input = item && item.querySelector('.js-qty__num');
    if (item) {
      item.style.transition = 'opacity .2s';
      item.style.opacity = '0.4';
      item.style.pointerEvents = 'none';
    }
    requestQty({
      key: key,
      input: input || document.createElement('input'),
      wrapper: input ? input.closest('.js-qty__wrapper') : null
    }, 0, true);
  }, true);

  // +/- buttons
  window.addEventListener('click', function (event) {
    var btn = event.target && event.target.closest && event.target.closest('.js-qty__adjust');
    if (!btn) return;
    var ctx = cartInputFrom(btn);
    if (!ctx) return; // not a cart line (e.g. product page selector) - leave alone

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    var isMinus = btn.classList.contains('js-qty__adjust--minus');
    requestQty(ctx, current(ctx) + (isMinus ? -1 : 1));
  }, true);

  // Quantity typed into the input
  window.addEventListener('change', function (event) {
    var input = event.target;
    if (!input || !input.classList || !input.classList.contains('js-qty__num')) return;
    var ctx = cartInputFrom(input);
    if (!ctx) return;

    event.stopImmediatePropagation();
    event.stopPropagation();

    var qty = parseQty(input.value);
    if (qty === null) {
      input.value = input.dataset.lastValidValue || 1;
      return;
    }
    requestQty(ctx, qty);
  }, true);

  // Keep "CART (n)" correct after any add-to-cart (product form, .bs-btn, upsell).
  document.addEventListener('ajaxProduct:added', refreshCounts);
})();